import { ArrowRight, LogOut } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Navigate, useNavigate } from "react-router-dom";
import { z } from "zod";
import { supabase } from "../../lib/supabase/client";
import { AsterGuide } from "../aster/AsterGuide";
import { useAuth } from "./AuthContext";

const onboardingSchema = z.object({
  firstName: z.string().trim().min(1, "Enter your first name.").max(80),
  lastName: z.string().trim().min(1, "Enter your last name.").max(80),
  displayName: z.string().trim().min(1, "Enter the name you want us to use.").max(100),
  pronouns: z.string().trim().max(50),
});

type OnboardingFields = z.infer<typeof onboardingSchema>;

export function OnboardingPage() {
  const auth = useAuth();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<OnboardingFields>();

  useEffect(() => {
    if (auth.profile) {
      reset({
        firstName: auth.profile.first_name ?? "",
        lastName: auth.profile.last_name ?? "",
        displayName: auth.profile.display_name ?? "",
        pronouns: auth.profile.pronouns ?? "",
      });
    }
  }, [auth.profile, reset]);

  if (!auth.user) return <Navigate to="/sign-in" replace />;
  if (auth.profile?.onboarding_complete) {
    return (
      <Navigate
        to={auth.profile.status === "approved" ? "/atlas" : `/account/${auth.profile.status}`}
        replace
      />
    );
  }

  const submit = handleSubmit(async (fields) => {
    const parsed = onboardingSchema.safeParse(fields);
    if (!parsed.success) {
      parsed.error.issues.forEach((issue) => {
        const field = issue.path[0] as keyof OnboardingFields;
        setError(field, { message: issue.message });
      });
      return;
    }
    if (!supabase || !auth.user) return;
    const { error } = await supabase.rpc("complete_onboarding", {
      p_first_name: parsed.data.firstName,
      p_last_name: parsed.data.lastName,
      p_display_name: parsed.data.displayName,
      p_pronouns: parsed.data.pronouns || null,
    });
    if (error) {
      setError("root", { message: "We could not save your profile. Please try again." });
      return;
    }
    await auth.refreshProfile();
    navigate("/atlas", { replace: true });
  });

  return (
    <section className="onboarding-page">
      <div className="onboarding-panel">
        <p className="eyebrow">Welcome to The Guided Pathway Foundation</p>
        <h1>How should we welcome you?</h1>
        <p>
          Complete your profile to begin Guiding Stars, our internal service, learning, and
          recognition program. Your private Atlas will open when you finish.
        </p>
        <AsterGuide
          context="onboarding"
          inline
          important
          appearanceKey="member-onboarding"
          message="This short profile helps the Foundation welcome you by name and prepare the right private Atlas access for your role."
          detail="You can update your profile and privacy choices later inside My Atlas."
        />
        <form onSubmit={(event) => void submit(event)} noValidate>
          {errors.root?.message && (
            <div className="error-summary" role="alert">
              {errors.root.message}
            </div>
          )}
          <div className="form-grid">
            <label>
              <span>First name</span>
              <input
                {...register("firstName")}
                autoComplete="given-name"
                aria-invalid={Boolean(errors.firstName)}
              />
              {errors.firstName && <small>{errors.firstName.message}</small>}
            </label>
            <label>
              <span>Last name</span>
              <input
                {...register("lastName")}
                autoComplete="family-name"
                aria-invalid={Boolean(errors.lastName)}
              />
              {errors.lastName && <small>{errors.lastName.message}</small>}
            </label>
            <label className="form-wide">
              <span>Display name</span>
              <input
                {...register("displayName")}
                autoComplete="nickname"
                aria-invalid={Boolean(errors.displayName)}
              />
              {errors.displayName && <small>{errors.displayName.message}</small>}
            </label>
            <label className="form-wide">
              <span>
                Pronouns <em>optional</em>
              </span>
              <input
                {...register("pronouns")}
                autoComplete="off"
                aria-invalid={Boolean(errors.pronouns)}
              />
              {errors.pronouns && <small>{errors.pronouns.message}</small>}
            </label>
          </div>
          <button className="button button-gold" type="submit" disabled={isSubmitting}>
            {isSubmitting ? "Saving…" : "Complete profile"} <ArrowRight size={17} />
          </button>
        </form>
        <button className="quiet-button" type="button" onClick={() => void auth.signOut()}>
          <LogOut size={15} /> Sign out
        </button>
      </div>
    </section>
  );
}
